/**
 * ERP sync for Contracts & Claims (S/4HANA, Ariba).
 *
 * Pull brings entities into staging for review. Push is deliberately absent
 * here: a claim or LD deduction goes outbound one record at a time from the
 * record itself, never from a bulk button.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Download, Loader2, Database, RefreshCw } from 'lucide-react';

const ENTITIES = [
  ['contracts', 'Contracts'], ['claims', 'Claims'], ['ld_trackers', 'LD tracking'],
  ['variations', 'Variations'], ['disputes', 'Disputes'],
  ['correspondences', 'Correspondences'],
];

export const ContractErpSyncPanel = ({ connector, label }) => {
  const [meta, setMeta] = useState(null);
  const [picked, setPicked] = useState(ENTITIES.map(([k]) => k));
  const [pulling, setPulling] = useState(false);
  const [results, setResults] = useState(null);
  const [peek, setPeek] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/connectors/contracts');
      setMeta((data.connectors || []).find(c => c.name === connector) || null);
    } catch { /* the console below already reports access problems */ }
  }, [connector]);

  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try {
      const { data } = await api.post(`/connectors/contracts/${connector}/pull`,
        { entities: picked });
      setResults(data.results || {});
      const ok = data.succeeded ?? Object.values(data.results || {}).filter(r => r.ok).length;
      const msg = `${ok} of ${data.attempted ?? picked.length} entities pulled`;
      // A pull where nothing came back is not a success, however green it looks.
      if (data.status === 'failed' || ok === 0) toast.error(`${msg} — see the errors below`);
      else if (data.status === 'partial') toast.warning(msg);
      else toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pull failed — check the connection below');
    } finally { setPulling(false); }
  };

  const open = async (entity) => {
    try {
      const { data } = await api.get(`/connectors/contracts/${connector}/staging`,
        { params: { entity, limit: 25 } });
      setPeek({ entity, ...data });
    } catch { toast.error('Could not read staging'); }
  };

  return (
    <div className="glass-card p-4" data-testid="contract-erp-sync-panel">
      <div className="flex items-center gap-2 mb-3">
        <Database className="w-4 h-4 text-sky-500" />
        <h3 className="text-sm font-semibold">Contracts &amp; Claims sync</h3>
        <Badge variant="secondary" className="text-[10px]">{label}</Badge>
        {meta?.pendingPush > 0 && (
          <Badge variant="outline" className="text-[10px]">{meta.pendingPush} queued outbound</Badge>
        )}
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs gap-1"
                onClick={pull} disabled={pulling || !picked.length}
                data-testid="contract-erp-pull">
          {pulling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          Pull selected
        </Button>
        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={load}
                data-testid="contract-erp-refresh">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
        {ENTITIES.map(([key, name]) => (
          <div key={key}
               className="flex items-center gap-2 text-xs border border-white/[0.06] rounded-lg px-2.5 py-1.5">
            <Checkbox checked={picked.includes(key)} disabled={pulling}
                      onCheckedChange={v => setPicked(p => v ? [...p, key] : p.filter(x => x !== key))}
                      data-testid={`contract-erp-pick-${key}`} />
            <button onClick={() => open(key)} className="text-left flex-1 truncate hover:text-sky-400"
                    data-testid={`contract-erp-open-${key}`}>
              {name}
            </button>
            <span className="text-[10px] text-slate-500 tabular-nums">
              {results?.[key]?.ok === false
                ? <span className="text-red-500">failed</span>
                : (meta?.staged?.[key] ?? 0)}
            </span>
          </div>
        ))}
      </div>

      {results && Object.entries(results).some(([, r]) => !r.ok) && (
        <div className="text-[11px] text-red-500 space-y-0.5 mb-2" data-testid="contract-erp-errors">
          {Object.entries(results).filter(([, r]) => !r.ok).map(([k, r]) => (
            <p key={k}>{k}: {r.error || `HTTP ${r.status}`}</p>
          ))}
        </div>
      )}

      {peek && (
        <div className="border border-white/[0.06] rounded-lg p-2" data-testid="contract-erp-staging">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            {peek.entity} — {peek.total} row(s) in staging
          </p>
          <ScrollArea className="h-40">
            <pre className="text-[10px] text-slate-400 whitespace-pre-wrap">
              {peek.rows?.length
                ? JSON.stringify(peek.rows.slice(0, 5).map(r => r.payload), null, 1)
                : 'Nothing staged for this entity yet — pull it first.'}
            </pre>
          </ScrollArea>
        </div>
      )}

      <p className="text-[10px] text-slate-500 mt-2">
        Pulled rows land in staging for review against the Panorix register. Sending a
        record the other way is done from the record itself, one at a time.
      </p>
    </div>
  );
};

export default ContractErpSyncPanel;
